/**
 * Onboarding Gate
 *
 * Answers one question for the whole app: does this user still have to finish the onboarding
 * wizard? While the answer is yes, ProtectedRoute keeps them on /onboarding — they cannot reach
 * the dashboard or anything else until the wizard is submitted.
 *
 * "Submitted" is the release point, not "approved": once the record is Submitted or UnderReview
 * the employee has nothing left to do, so they are let through. If HR sends it back
 * (RevisionRequested) the gate closes again. That is exactly what onboardingNeedsAction encodes.
 */

import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from '@hooks/useAuth';
import { getOnboardingStatus, onboardingNeedsAction } from '@/features/onboarding/services/onboardingService';

const CACHE_KEY = 'hrms_onboarding_gate';

interface OnboardingGateContextType {
  /** True while the wizard still needs the employee's input. */
  needsOnboarding: boolean;
  /** False until the status is known — routes must wait rather than guess. */
  isResolved: boolean;
  /** Re-check with the server. Call after submitting the wizard to open the gate. */
  refresh: () => Promise<void>;
  clear: () => void;
}

const OnboardingGateContext = createContext<OnboardingGateContextType | undefined>(undefined);

export function OnboardingGateProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [isResolved, setIsResolved] = useState(false);

  const resolve = useCallback(async (bypassCache = false) => {
    // No employee record (e.g. a tenant admin) means there is no wizard to complete.
    if (!isAuthenticated || !user?.employeeId) {
      setNeedsOnboarding(false);
      setIsResolved(true);
      return;
    }

    const cacheKey = `${CACHE_KEY}_${user.employeeId}`;

    if (!bypassCache) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached !== null) {
        setNeedsOnboarding(cached === 'true');
        setIsResolved(true);
        return;
      }
    }

    try {
      const status = await getOnboardingStatus(user.employeeId);
      const pending = onboardingNeedsAction(status.status);
      setNeedsOnboarding(pending);
      sessionStorage.setItem(cacheKey, String(pending));
    } catch (error) {
      const httpStatus = (error as { status?: number })?.status;

      if (httpStatus === 404) {
        // No record yet — the wizard has not been started. LoginPage already treats this as
        // "go and onboard", and the wizard initiates the record on arrival, so the gate agrees.
        setNeedsOnboarding(true);
        sessionStorage.setItem(cacheKey, 'true');
      } else {
        // Fail open on anything else. A network blip or a 500 must not lock someone out of the
        // whole app on a guess — and it is not cached, so the next navigation re-checks.
        console.warn('[OnboardingGate] Could not resolve onboarding status; allowing navigation.', error);
        setNeedsOnboarding(false);
      }
    } finally {
      setIsResolved(true);
    }
  }, [isAuthenticated, user?.employeeId]);

  const clear = useCallback(() => {
    if (user?.employeeId) sessionStorage.removeItem(`${CACHE_KEY}_${user.employeeId}`);
    setNeedsOnboarding(false);
    setIsResolved(false);
  }, [user?.employeeId]);

  const refresh = useCallback(async () => {
    if (user?.employeeId) sessionStorage.removeItem(`${CACHE_KEY}_${user.employeeId}`);
    await resolve(true);
  }, [resolve, user?.employeeId]);

  useEffect(() => { resolve(); }, [resolve]);

  return (
    <OnboardingGateContext.Provider value={{ needsOnboarding, isResolved, refresh, clear }}>
      {children}
    </OnboardingGateContext.Provider>
  );
}

export function useOnboardingGate(): OnboardingGateContextType {
  const context = useContext(OnboardingGateContext);
  if (!context) {
    throw new Error('useOnboardingGate must be used within an OnboardingGateProvider');
  }
  return context;
}
